/**
 * Utilities for detecting hard Excel formula errors in read-back results.
 *
 * These helpers are deliberately pure so both the executor loop and the final
 * UI safety pass can share the same definition of "not safe to call done".
 */

import { columnIndex, numberValue, stringValue } from "./excelValueUtils";
import { isFdsBoundFormula } from "./formulaWriteDetection";

export const EXCEL_HARD_ERROR_TOKENS = [
  "#NULL!",
  "#NUM!",
  "#N/A",
  "#VALUE!",
  "#REF!",
  "#DIV/0!",
  "#NAME?",
  "#CALC!",
  "#SPILL!",
] as const;

const EXCEL_HARD_ERROR_ALIASES: Readonly<Record<string, string>> = {
  "#NULL": "#NULL!",
  "#NUM": "#NUM!",
  "#VALUE": "#VALUE!",
  "#REF": "#REF!",
  "#DIV/0": "#DIV/0!",
  "#CALC": "#CALC!",
  "#SPILL": "#SPILL!",
};

export interface ExcelHardErrorCell {
  sheetName: string;
  address: string;
  token: string;
  value: string;
}

export interface ExcelHardErrorScanResult {
  errors: ExcelHardErrorCell[];
}

interface ReadRangeLike {
  sheetName?: unknown;
  address?: unknown;
  startRow?: unknown;
  startColumn?: unknown;
  values?: unknown;
  csv?: unknown;
}

interface ClassifyValuesOptions {
  sheetName?: string;
  address?: string;
  startRow?: number;
  startColumn?: number;
}

export function classifyHardErrorsInReadRangeResult(
  raw: unknown,
): ExcelHardErrorScanResult {
  const result = parseObject(raw);
  if (!result) return { errors: [] };

  const sheetName = stringValue(result.sheetName) || "Sheet";
  const address = stringValue(result.address);
  const startRow = numberValue(result.startRow);
  const startColumn = numberValue(result.startColumn);
  const opts = { sheetName, address, startRow, startColumn };

  if (Array.isArray(result.values)) {
    return classifyHardErrorsInValues(result.values, opts);
  }

  const csv = stringValue(result.csv);
  if (csv !== undefined) {
    return classifyHardErrorsInValues(parseCsv(csv), opts);
  }

  return { errors: [] };
}

export function classifyHardErrorsInValues(
  values: unknown[][],
  opts: ClassifyValuesOptions,
): ExcelHardErrorScanResult {
  return classifyHardErrorsInCells(values, opts);
}

export function classifyHardErrorsInFormulaValues(
  values: unknown[][],
  formulas: unknown[][] | undefined,
  opts: ClassifyValuesOptions,
): ExcelHardErrorScanResult {
  const result = classifyHardErrorsInCells(values, opts, (rowIndex, columnIndex) => {
    const formula = formulas?.[rowIndex]?.[columnIndex];
    const value = values[rowIndex]?.[columnIndex];
    return (
      typeof formula === "string" &&
      formula.trim().startsWith("=") &&
      !formulaReturnsErrorTokenAsText(formula, value)
    );
  });
  const existingAddresses = new Set(
    result.errors.map((error) => error.address.toUpperCase()),
  );
  const sheetName = opts.sheetName || "Sheet";
  const topLeft = parseTopLeftAddress(opts.address);
  const startRow = opts.startRow ?? topLeft?.row ?? 0;
  const startColumn = opts.startColumn ?? topLeft?.column ?? 0;
  for (let rowIndex = 0; rowIndex < (formulas?.length ?? 0); rowIndex += 1) {
    const formulaRow = formulas?.[rowIndex];
    if (!Array.isArray(formulaRow)) continue;
    for (let columnIndex = 0; columnIndex < formulaRow.length; columnIndex += 1) {
      const formula = formulaRow[columnIndex];
      const value = values[rowIndex]?.[columnIndex];
      if (!isFdsBoundFormula(formula) || !isBlankFormulaValue(value)) continue;
      const address =
        `${sheetName}!${toA1Address(startRow + rowIndex, startColumn + columnIndex)}`;
      if (existingAddresses.has(address.toUpperCase())) continue;
      result.errors.push({
        sheetName,
        address,
        token: "BLANK",
        value: "",
      });
    }
  }
  return result;
}

function classifyHardErrorsInCells(
  values: unknown[][],
  opts: ClassifyValuesOptions,
  shouldIncludeCell?: (rowIndex: number, columnIndex: number) => boolean,
): ExcelHardErrorScanResult {
  const errors: ExcelHardErrorCell[] = [];
  const sheetName = opts.sheetName || "Sheet";
  const topLeft = parseTopLeftAddress(opts.address);
  const startRow = opts.startRow ?? topLeft?.row ?? 0;
  const startColumn = opts.startColumn ?? topLeft?.column ?? 0;

  for (let rowIndex = 0; rowIndex < values.length; rowIndex += 1) {
    const row = values[rowIndex];
    if (!Array.isArray(row)) continue;
    for (let columnIndex = 0; columnIndex < row.length; columnIndex += 1) {
      if (shouldIncludeCell && !shouldIncludeCell(rowIndex, columnIndex)) continue;
      const value = row[columnIndex];
      const token = hardErrorToken(value);
      if (!token) continue;
      errors.push({
        sheetName,
        address: `${sheetName}!${toA1Address(startRow + rowIndex, startColumn + columnIndex)}`,
        token,
        value: String(value),
      });
    }
  }

  return { errors };
}

export function summarizeHardErrors(
  errors: readonly ExcelHardErrorCell[],
  maxItems = 5,
): string {
  if (errors.length === 0) return "";
  const sample = errors
    .slice(0, maxItems)
    .map((error) => `${error.address} ${error.token}`)
    .join(", ");
  const suffix = errors.length > maxItems ? ` and ${errors.length - maxItems} more` : "";
  return `${sample}${suffix}`;
}

function hardErrorToken(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toUpperCase();
  return (
    EXCEL_HARD_ERROR_TOKENS.find((token) => normalized === token) ??
    EXCEL_HARD_ERROR_ALIASES[normalized]
  );
}

function formulaReturnsErrorTokenAsText(formula: string, value: unknown): boolean {
  const token = hardErrorToken(value);
  const literal = /^="([^"]+)"$/i.exec(formula.trim())?.[1];
  return token !== undefined && hardErrorToken(literal) === token;
}

function isBlankFormulaValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  );
}

function parseObject(raw: unknown): ReadRangeLike | undefined {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as ReadRangeLike;
  }
  if (typeof raw !== "string") return undefined;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as ReadRangeLike
      : undefined;
  } catch {
    return undefined;
  }
}

function toA1Address(zeroBasedRow: number, zeroBasedColumn: number): string {
  return `${columnName(zeroBasedColumn)}${zeroBasedRow + 1}`;
}

function parseTopLeftAddress(address: string | undefined): { row: number; column: number } | undefined {
  if (!address) return undefined;
  const rangePart = address.includes("!") ? address.slice(address.lastIndexOf("!") + 1) : address;
  const firstCell = rangePart.split(":")[0]?.replace(/\$/g, "").trim();
  const match = /^([A-Z]+)(\d+)$/i.exec(firstCell ?? "");
  if (!match) return undefined;
  return {
    row: Number(match[2]) - 1,
    column: columnIndex(match[1]),
  };
}

function columnName(zeroBasedColumn: number): string {
  let n = Math.max(0, Math.floor(zeroBasedColumn)) + 1;
  let name = "";
  while (n > 0) {
    const remainder = (n - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

function parseCsv(csv: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < csv.length; i += 1) {
    const char = csv[i];
    if (inQuotes) {
      if (char === '"' && csv[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else if (char !== "\r") {
      cell += char;
    }
  }

  row.push(cell);
  rows.push(row);
  return rows;
}
